import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { createAppAsyncThunk, AppThunk } from "appThunk";
import { RootState } from "store";

import { DataWrapper } from "domain/dataWrapper";

import {
    selectStores,
    selectRetailCentresWithScores,
    selectTarget
} from "modules/customer/tools/location/locationSlice";
import {
    SuggestedNewLocation,
    loadSuggestedNewLocations
} from "modules/customer/tools/location/suggestedNewLocations/suggestedNewLocation";
import { logError } from "modules/helpers/logger/loggerSlice";

interface SuggestedNewLocationsVisibility {
    isVisible: boolean
}

interface SuggestedNewLocationsState {
    suggestedNewLocations: DataWrapper<SuggestedNewLocation[]>,
    suggestedNewLocationsVisibility: SuggestedNewLocationsVisibility,
    includeOnlyRetailCentres: boolean,
    includeExistingStoreLocations: boolean
}

const initialState: SuggestedNewLocationsState = {
    suggestedNewLocations: { isLoading: false, hasErrors: false, data: [] },
    suggestedNewLocationsVisibility: {
        isVisible: false
    },
    includeOnlyRetailCentres: true,
    includeExistingStoreLocations: false
};

const suggestedNewLocationsSlice = createSlice({
    name: "customer/tools/location/suggestedNewLocations",
    initialState,
    reducers: {
        clearIncludeExistingStoreLocations: (state) => {
            state.includeExistingStoreLocations = initialState.includeExistingStoreLocations;
        },
        clearIncludeOnlyRetailCentres: (state) => {
            state.includeOnlyRetailCentres = initialState.includeOnlyRetailCentres;
        },
        hideSuggestedNewLocations: (state) => {
            state.suggestedNewLocationsVisibility = initialState.suggestedNewLocationsVisibility;
        },
        showSuggestedNewLocations: (state) => {
            state.suggestedNewLocationsVisibility.isVisible = true;
        },
        toggleIncludeExistingStoreLocations: (state) => {
            state.includeExistingStoreLocations = !state.includeExistingStoreLocations;
        },
        toggleIncludeOnlyRetailCentres: (state) => {
            state.includeOnlyRetailCentres = !state.includeOnlyRetailCentres;
        }
    },
    extraReducers: (builder: any) => {
        builder.addCase(loadSuggestedNewLocationsSetup.pending, (state: SuggestedNewLocationsState) => {
            state.suggestedNewLocations.isLoading = true;
            state.suggestedNewLocations.hasErrors = false;
            state.suggestedNewLocations.data = initialState.suggestedNewLocations.data;
        });
        builder.addCase(loadSuggestedNewLocationsSetup.rejected, (state: SuggestedNewLocationsState) => {
            state.suggestedNewLocations.isLoading = false;
            state.suggestedNewLocations.hasErrors = true;
            state.suggestedNewLocations.data = initialState.suggestedNewLocations.data;
        });
        builder.addCase(loadSuggestedNewLocationsSetup.fulfilled, (state: SuggestedNewLocationsState, action: PayloadAction<SuggestedNewLocation[]>) => {
            state.suggestedNewLocations.isLoading = false;
            state.suggestedNewLocations.hasErrors = false;
            state.suggestedNewLocations.data = action.payload;
        });
    }
});

export const {
    clearIncludeExistingStoreLocations,
    clearIncludeOnlyRetailCentres,
    hideSuggestedNewLocations,
    showSuggestedNewLocations,
    toggleIncludeExistingStoreLocations,
    toggleIncludeOnlyRetailCentres
} = suggestedNewLocationsSlice.actions;

export const loadSuggestedNewLocationsSetup = createAppAsyncThunk(
    "customer/tools/location/suggestedNewLocations/loadSuggestedNewLocationsSetup",
    async (arg, thunkAPI) => {
        try {
            const state = thunkAPI.getState();
            const includeExistingStoreLocations = selectIncludeExistingStoreLocations(state);
            const includeOnlyRetailCentres = selectIncludeOnlyRetailCentres(state);
            const retailCentresWithScores = selectRetailCentresWithScores(state);
            const stores = selectStores(state);
            const target = selectTarget(state);

            const distinctStoreRetailCentreIds = Array.from(new Set(stores.map(item => item.retailCentre.id)));

            // IncludeOnlyRetailCentres filter
            let filteredRetailCentresWithScores = includeOnlyRetailCentres
                ? retailCentresWithScores.filter(rc => rc.isInRetailCentre === true)
                : retailCentresWithScores;

            // ExistingStoreLocations filter
            filteredRetailCentresWithScores = includeExistingStoreLocations
                ? filteredRetailCentresWithScores
                : filteredRetailCentresWithScores.filter(rc => !distinctStoreRetailCentreIds.includes(rc.id));

            const suggestedNewLocations = await thunkAPI.dispatch(loadSuggestedNewLocations(filteredRetailCentresWithScores, target));
            return suggestedNewLocations;
        } catch (error) {
            thunkAPI.dispatch(logError("Error loading Suggested New Locations setup.", error));
            return thunkAPI.rejectWithValue([]);
        }
    }
);

export const clearSuggestedNewLocations = (): AppThunk => async (dispatch) => {
    dispatch(suggestedNewLocationsSlice.actions.clearIncludeExistingStoreLocations());
    dispatch(suggestedNewLocationsSlice.actions.clearIncludeOnlyRetailCentres());
    dispatch(suggestedNewLocationsSlice.actions.hideSuggestedNewLocations());
};

export const selectSuggestedNewLocations = (state: RootState) => {
    return state.customer.tools.location.suggestedNewLocations.suggestedNewLocations;
};

export const selectSuggestedNewLocationsVisibility = (state: RootState) => {
    return state.customer.tools.location.suggestedNewLocations.suggestedNewLocationsVisibility;
};

export const selectIncludeExistingStoreLocations = (state: RootState) => {
    return state.customer.tools.location.suggestedNewLocations.includeExistingStoreLocations;
};

export const selectIncludeOnlyRetailCentres = (state: RootState) => {
    return state.customer.tools.location.suggestedNewLocations.includeOnlyRetailCentres;
};

export default suggestedNewLocationsSlice;
